<script setup>
import { ref, watch, computed, onUnmounted } from "vue";
import welcomeTypoLogo from "@/assets/logo-new-typo-dark.svg";
import { IDIOMA, IDIOMA_BASE, enIdioma } from "@/i18n/idioma";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

// Fuera del español, los términos llevan el aviso de que la versión que vale es
// la española.
const enOtroIdioma = IDIOMA !== IDIOMA_BASE;

// Las listas de los términos (`legal.bienvenida`): el orden es el del texto.
const TERMINOS = {
  servicio: ["directo", "votacion", "noticias", "redes"],
  usoPermitido: ["escuchar", "votacion", "comunidad", "compartir"],
  prohibidas: ["copiar", "ilegales", "restringidas", "votacion", "ingenieria", "bots"],
  votacion: ["anonimos", "validar", "resultados"],
  responsabilidad: ["disponible", "calidad", "resultados"],
};

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["accept"]);

const view = ref("welcome");

const backgroundStyle = computed(() => ({
  "--welcome-modal-logo": `url("${welcomeTypoLogo}")`,
}));

const openTerms = () => {
  view.value = "terms";
};

const backToWelcome = () => {
  view.value = "welcome";
};

const acceptWelcome = () => {
  emit("accept");
};

const handleKeydown = (event) => {
  // Keep acceptance mandatory: only navigate back with Escape from terms.
  if (event.key === "Escape" && view.value === "terms") {
    backToWelcome();
  }
};

watch(
  () => props.show,
  (isOpen) => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeydown);
      document.body.style.overflow = "hidden";
      view.value = "welcome";
      return;
    }

    document.removeEventListener("keydown", handleKeydown);
    document.body.style.overflow = "";
  },
  { immediate: true },
);

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
  document.body.style.overflow = "";
});
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show" class="modal-backdrop" role="dialog" aria-modal="true">
        <div
          class="welcome-modal"
          :class="{ 'welcome-modal-terms': view === 'terms' }"
          :style="backgroundStyle"
        >
          <div v-if="view === 'welcome'" class="welcome-modal-content">
            <header class="welcome-header">
              <div class="welcome-header-copy">
                <h2>{{ t("marco.bienvenida.titulo") }}</h2>
                <p class="welcome-subtitle">{{ t("marco.bienvenida.lema") }}</p>
              </div>
            </header>

            <!-- Astra FM, no Astralab (Pablo, 26.10.26): la marca anterior seguía
                 aquí, y Google lo ve encima de cada página al renderizarla. -->
            <p>{{ t("marco.bienvenida.radio") }}</p>
            <p>{{ t("marco.bienvenida.edad") }}</p>
            <p>
              <i18n-t keypath="marco.bienvenida.aceptas" scope="global">
                <template #terminos>
                  <button class="welcome-terms-link" @click="openTerms">{{
                    t("marco.bienvenida.terminos")
                  }}</button>
                </template>
              </i18n-t>
            </p>

            <button class="welcome-enter-btn" @click="acceptWelcome">
              {{ t("marco.bienvenida.entrar") }}
            </button>
          </div>

          <div v-else class="welcome-modal-content welcome-terms-content">
            <header class="welcome-header welcome-header-terms">
              <div class="welcome-header-copy">
                <!-- 🔴 Los términos de aquí abajo (tanda 4, `legal.bienvenida`) son la
                     versión ANTIGUA, sin tildes y con el sistema «Riot», y no
                     coinciden con /legal/terms: por eso llevan claves propias y no
                     las de `legal.terminos`. El español sigue letra por letra como
                     estaba; los otros tres idiomas traducen ese mismo texto. -->
                <h2>{{ t("legal.bienvenida.titulo") }}</h2>
                <p class="welcome-subtitle">{{ t("marco.bienvenida.lema") }}</p>
              </div>
            </header>

            <!-- Fuera del español, la versión que vale es la española (Pablo,
                 04.11.26). En la raíz no se pinta. -->
            <p v-if="enOtroIdioma" class="welcome-terms-aviso">{{ t("legal.aviso") }}</p>

            <div class="welcome-terms-scroll">
              <section>
                <h3>{{ t("legal.bienvenida.aceptacion.titulo") }}</h3>
                <p>
                  {{ t("legal.bienvenida.aceptacion.texto") }}
                </p>
              </section>

              <section>
                <h3>{{ t("legal.bienvenida.edad.titulo") }}</h3>
                <p>
                  <strong>{{ t("legal.bienvenida.edad.destacado") }}</strong>
                  {{ t("legal.bienvenida.edad.texto") }}
                </p>
              </section>

              <section>
                <h3>{{ t("legal.bienvenida.servicio.titulo") }}</h3>
                <p>
                  {{ t("legal.bienvenida.servicio.intro") }}
                </p>
                <ul>
                  <li v-for="k in TERMINOS.servicio" :key="k">
                    {{ t(`legal.bienvenida.servicio.lista.${k}`) }}
                  </li>
                </ul>
              </section>

              <section>
                <h3>{{ t("legal.bienvenida.propiedad.titulo") }}</h3>
                <p>
                  {{ t("legal.bienvenida.propiedad.p1") }}
                </p>
                <p>
                  {{ t("legal.bienvenida.propiedad.p2") }}
                </p>
              </section>

              <section>
                <h3>{{ t("legal.bienvenida.usoPermitido.titulo") }}</h3>
                <p>{{ t("legal.bienvenida.usoPermitido.intro") }}</p>
                <ul>
                  <li v-for="k in TERMINOS.usoPermitido" :key="k">
                    {{ t(`legal.bienvenida.usoPermitido.lista.${k}`) }}
                  </li>
                </ul>
              </section>

              <section>
                <h3>{{ t("legal.bienvenida.prohibidas.titulo") }}</h3>
                <p>{{ t("legal.bienvenida.prohibidas.intro") }}</p>
                <ul>
                  <li v-for="k in TERMINOS.prohibidas" :key="k">
                    {{ t(`legal.bienvenida.prohibidas.lista.${k}`) }}
                  </li>
                </ul>
              </section>

              <section>
                <h3>{{ t("legal.bienvenida.votacion.titulo") }}</h3>
                <p>
                  {{ t("legal.bienvenida.votacion.intro") }}
                </p>
                <ul>
                  <li v-for="k in TERMINOS.votacion" :key="k">
                    {{ t(`legal.bienvenida.votacion.lista.${k}`) }}
                  </li>
                </ul>
              </section>

              <section>
                <h3>{{ t("legal.bienvenida.privacidad.titulo") }}</h3>
                <i18n-t keypath="legal.bienvenida.privacidad.texto" scope="global" tag="p">
                  <template #enlace>
                    <a
                      :href="enIdioma('/legal/privacy', IDIOMA)"
                      target="_blank"
                      rel="noopener noreferrer"
                    >{{ t("legal.bienvenida.privacidad.enlace") }}</a>
                  </template>
                </i18n-t>
              </section>

              <section>
                <h3>{{ t("legal.bienvenida.modificaciones.titulo") }}</h3>
                <p>
                  {{ t("legal.bienvenida.modificaciones.texto") }}
                </p>
              </section>

              <section>
                <h3>{{ t("legal.bienvenida.responsabilidad.titulo") }}</h3>
                <p>
                  {{ t("legal.bienvenida.responsabilidad.intro") }}
                </p>
                <ul>
                  <li v-for="k in TERMINOS.responsabilidad" :key="k">
                    {{ t(`legal.bienvenida.responsabilidad.lista.${k}`) }}
                  </li>
                </ul>
                <p>
                  {{ t("legal.bienvenida.responsabilidad.cierre") }}
                </p>
              </section>

              <section>
                <h3>{{ t("legal.bienvenida.terceros.titulo") }}</h3>
                <p>
                  {{ t("legal.bienvenida.terceros.texto") }}
                </p>
              </section>

              <section>
                <h3>{{ t("legal.bienvenida.legislacion.titulo") }}</h3>
                <p>
                  {{ t("legal.bienvenida.legislacion.texto") }}
                </p>
              </section>

              <section>
                <h3>{{ t("legal.bienvenida.contacto.titulo") }}</h3>
                <!-- ⚠️ En español sale «contacto .», con espacio antes del punto:
                     así se veía antes de traducir y no se toca aquí. -->
                <i18n-t keypath="legal.bienvenida.contacto.texto" scope="global" tag="p">
                  <template #enlace>
                    <a
                      :href="enIdioma('/legal/contact', IDIOMA)"
                      target="_blank"
                      rel="noopener noreferrer"
                    >{{ t("legal.bienvenida.contacto.enlace") }}</a>
                  </template>
                </i18n-t>
              </section>
            </div>

            <button
              class="welcome-back-btn welcome-back-btn-floating"
              @click="backToWelcome"
            >
              {{ t("marco.bienvenida.volver") }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
@use "@/assets/scss/type" as type;
@import "@/assets/scss/astra-vars.scss";

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 10000;
  background-color: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(5px);
}

.welcome-modal {
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 760px;
  max-height: calc(100vh - 2rem);
  background: var(--bg-color);
  border: 1px solid var(--border);
  border-radius: 0;
}

.welcome-modal::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(
      145deg,
      rgba(12, 18, 26, 0.86) 0%,
      rgba(12, 18, 26, 0.64) 100%
    ),
    var(--welcome-modal-logo);
  background-repeat: no-repeat;
  background-position: center 42%;
  background-size: min(85%, 560px) auto;
  opacity: 0.16;
  pointer-events: none;
}

html.light .welcome-modal::before {
  background-image:
    linear-gradient(
      145deg,
      rgba(255, 255, 255, 0.93) 0%,
      rgba(249, 250, 252, 0.9) 100%
    ),
    var(--welcome-modal-logo);
  opacity: 0.28;
}

.welcome-modal-terms {
  max-width: 980px;
}

.welcome-modal-content {
  position: relative;
  z-index: 1;
  overflow-y: auto;
  max-height: calc(100vh - 2rem);
  padding: $s34;

  h2 {
    margin: 0 0 $s16;
    color: var(--text-color);
    font-size: $s34;
    line-height: 1.1;
  }

  p {
    margin: 0 0 $s12;
    color: var(--text-color);
    line-height: 1.6;
    opacity: 0.9;
  }
}

.welcome-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: $s24;
  padding-bottom: $s16;
  border-bottom: 1px solid var(--border);
}

.welcome-header-copy {
  min-width: 0;
}

.welcome-subtitle {
  margin: $s04 0 0 !important;
  font-size: $s14;
  font-style: italic;
  opacity: 0.72;
}

.welcome-terms-link {
  // 🔴 En línea con el texto: un estilo global de los botones lo ponía en
  // `display: flex` y el enlace y el punto final caían cada uno en su línea.
  display: inline;
  border: 0;
  background: transparent;
  color: var(--accent-color);
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  font-size: inherit;
  line-height: inherit;
}

.welcome-enter-btn,
.welcome-back-btn {
  margin-top: $s18;
  border: 1px solid var(--border);
  border-radius: 0;
  background: var(--accent-color);
  color: #fff;
  padding: $s10 $s18;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    filter 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }
}

.welcome-back-btn {
  background: var(--bg-color-grid);
  color: var(--text-color);
}

.welcome-terms-content {
  position: relative;
  padding-bottom: $s42;
}

// El aviso de traducción, el tono del aviso de idioma de Actualidad. Con dos
// clases para ganarle a `.welcome-modal-content p`.
.welcome-modal-content .welcome-terms-aviso {
  @include type.mono-label;
  margin: 0 0 $s12;
  color: var(--color-text-tertiary);
  opacity: 1;
}

.welcome-terms-scroll {
  border: 1px solid var(--border);
  border-radius: 0;
  overflow-y: auto;
  height: 58vh;
  max-height: 620px;
  padding: $s16;
  background: var(--bg-color-grid);

  section {
    margin-bottom: $s16;
  }

  h3 {
    margin: 0 0 $s08;
    color: var(--accent-color);
    font-size: $s16;
  }

  ul {
    margin: 0 0 $s12;
    padding-left: $s24;
    color: var(--text-color);
  }

  a {
    color: var(--accent-color);
    text-decoration: underline;
  }
}

.welcome-back-btn-floating {
  position: absolute;
  right: $s24;
  bottom: $s18;
  z-index: 2;
}

@media (max-width: 768px) {
  .welcome-modal-terms {
    max-width: 100%;
  }

  .welcome-modal-content {
    padding: $s18;

    h2 {
      font-size: $s24;
    }
  }

  .welcome-terms-scroll {
    height: 52vh;
  }

  .welcome-back-btn-floating {
    right: $s16;
    bottom: $s16;
  }
}
</style>
